import type { ReactNode } from 'react'
import { Spinner } from './LoadingState'

/**
 * The primary action, with a spinner. Mirrors wechat/src/components/Button.jsx:
 * `loading` shows the spinner AND blocks re-taps, because a double-tapped
 * booking is a real problem, not a cosmetic one.
 *
 * ---------------------------------------------------------------------------
 * SIZE IS A PROP, NOT AN OVERRIDE CLASS.
 *
 * The card row used to be built by appending `px-3 py-0 text-13` after the
 * base `px-5 py-3.5 text-base`, which produced markup carrying BOTH:
 *
 *     ... rounded-pill px-5 py-3.5 text-base ... px-3 py-0 text-13
 *
 * Tailwind resolves that by order in the STYLESHEET, not order in the class
 * attribute — so `px-5` and `py-3.5` kept winning and the row buttons came out
 * oversized with far too much padding. Appending a conflicting utility is not
 * an override; it is a coin flip. Choosing the classes here means only one of
 * each ever reaches the DOM.
 *
 * `row` reproduces `.btn-row .btn` from wechat/src/styles/components.css:
 *     min-height: 42px; padding: 0 12px; font-size: 13px; letter-spacing: .04em
 * expressed in rem so it tracks the root scale, exactly like every other size
 * in this app. A literal 42px would stay put while the text around it grew.
 */
export default function Button({
  children, onClick, loading = false, disabled = false,
  variant = 'primary', size = 'default', className = ''
}: {
  children: ReactNode
  onClick?: () => void
  loading?: boolean
  disabled?: boolean
  variant?: 'primary' | 'ghost' | 'danger'
  /** `row` = the compact button used inside an event card's action row. */
  size?: 'default' | 'row'
  className?: string
}) {
  const layout = 'inline-flex items-center justify-center gap-2 rounded-pill font-bold uppercase transition-opacity active:opacity-80'

  const sizing = {
    // .btn — padding: 14px; font-size: 14px; letter-spacing: .1em
    default: 'w-full px-5 py-3.5 text-base tracking-btn',
    // .btn-row .btn — 42px tall, 12px sides, no vertical padding, 13px text
    row: 'min-h-[2.625rem] flex-1 px-3 py-0 text-13 tracking-[0.04em]'
  }[size]

  const skin = {
    primary: 'bg-gold text-[#1a1208]',
    ghost: 'border border-gold-dim bg-transparent text-ivory',
    danger: 'border border-[#e08a6a] bg-transparent text-[#e08a6a]'
  }[variant]

  const dead = disabled || loading

  return (
    <button
      type="button"
      disabled={dead}
      onClick={() => { if (!dead && onClick) onClick() }}
      className={`${layout} ${sizing} ${skin} ${dead ? 'opacity-45' : ''} ${className}`}
    >
      {loading ? <Spinner /> : null}
      {children}
    </button>
  )
}
